import React, { useEffect, useState } from "react";
import Moment from "moment";

import { Badge, Button, Checkbox, ConfirmDialog, DetailModal } from "../ui";
import SaleMarkPartReceivedModal from "./SaleMarkPartReceivedModal";

const SaleEditPartModal = ({
    open,
    onClose,
    editForm,
    part,
    partIndex,
    onSavePart,
    onRemove,
    onFieldBlur,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftPart, setDraftPart] = useState(null);
    const [confirmReceivedOpen, setConfirmReceivedOpen] = useState(false);
    const [confirmReceivedSaving, setConfirmReceivedSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setDeleteConfirmOpen(false);
        }
    }, [open]);

    useEffect(() => {
        if (!part) {
            setDraftPart(null);
            setIsEditing(false);
            return;
        }
        setDraftPart({
            dateOrdered: part.dateOrdered || "",
            quantity: part.quantity || "",
            supplier: part.supplier || "",
            item: part.item || "",
            note: part.note || "",
            itemReceived: Boolean(part.itemReceived),
            dateReceived: part.dateReceived || "",
        });
        setIsEditing(false);
    }, [part, open]);

    if (!part) return null;

    const handleDraftChange = (field, value) => {
        setDraftPart((prev) => {
            const next = { ...prev, [field]: value };
            if (field === "itemReceived" && !value) {
                next.dateReceived = "";
            }
            return next;
        });
    };

    const handleCancelEdit = () => {
        setDraftPart({
            dateOrdered: part.dateOrdered || "",
            quantity: part.quantity || "",
            supplier: part.supplier || "",
            item: part.item || "",
            note: part.note || "",
            itemReceived: Boolean(part.itemReceived),
            dateReceived: part.dateReceived || "",
        });
        setIsEditing(false);
    };

    const handleSaveEdit = async () => {
        await onSavePart(partIndex, draftPart);
        setIsEditing(false);
    };

    const handleConfirmMarkReceived = async (dateReceivedYmd) => {
        const base = draftPart || {
            dateOrdered: part.dateOrdered || "",
            quantity: part.quantity || "",
            supplier: part.supplier || "",
            item: part.item || "",
            note: part.note || "",
            itemReceived: false,
            dateReceived: "",
        };
        const next = {
            ...base,
            itemReceived: true,
            dateReceived: dateReceivedYmd,
        };
        setDraftPart(next);
        setConfirmReceivedSaving(true);
        try {
            await onSavePart(partIndex, next);
            setConfirmReceivedOpen(false);
            if (typeof onFieldBlur === "function") {
                onFieldBlur();
            }
        } finally {
            setConfirmReceivedSaving(false);
        }
    };

    const readOnlyValue = (value) =>
        value === null || value === undefined || String(value).trim() === "" ? "—" : value;

    const statusReceived = Boolean(draftPart?.itemReceived);

    const dateReceivedRaw =
        draftPart?.dateReceived !== undefined && draftPart.dateReceived !== ""
            ? draftPart.dateReceived
            : (part.dateReceived ?? "");
    const dateReceivedDisplay = (() => {
        if (dateReceivedRaw == null || String(dateReceivedRaw).trim() === "") {
            return "—";
        }
        const parsed = Moment(dateReceivedRaw);
        return parsed.isValid() ? parsed.format("LL") : String(dateReceivedRaw);
    })();

    const dateReceivedInputValue = (() => {
        const raw = draftPart?.dateReceived;
        if (raw == null || String(raw).trim() === "") return "";
        const m = Moment(raw);
        return m.isValid() ? m.format("YYYY-MM-DD") : "";
    })();

    /** Show received block in view mode only when received; in edit mode always (so Ordered lines can be marked). */
    const showReceivedSection = statusReceived || isEditing;
    /** Neutral styling when editing a part that is still Ordered */
    const receivedSectionPending = isEditing && !statusReceived;

    const dismissPartModal = () => {
        if (confirmReceivedOpen) {
            if (!confirmReceivedSaving) setConfirmReceivedOpen(false);
            return;
        }
        if (deleteConfirmOpen) {
            setDeleteConfirmOpen(false);
            return;
        }
        onClose();
    };

    const handleConfirmDeletePart = () => {
        setDeleteConfirmOpen(false);
        onRemove(partIndex);
    };

    return (
        <DetailModal
            open={open}
            onClose={dismissPartModal}
            title={
                <span className="Sale-part-modal-title-row">
                    Part Details
                    <Badge tone={statusReceived ? "success" : "warning"}>
                        {statusReceived ? "Received" : "Ordered"}
                    </Badge>
                </span>
            }
            subtitle="View and edit selected part"
            size="md"
            topLayer={
                <>
                    <SaleMarkPartReceivedModal
                        open={confirmReceivedOpen}
                        onClose={() => !confirmReceivedSaving && setConfirmReceivedOpen(false)}
                        loading={confirmReceivedSaving}
                        onConfirm={handleConfirmMarkReceived}
                    />
                    <ConfirmDialog
                        nested
                        open={deleteConfirmOpen}
                        title="Delete this part?"
                        description="This line will be removed from the sale's parts list. You can't undo this action."
                        confirmLabel="Delete"
                        cancelLabel="Cancel"
                        confirmVariant="danger"
                        onConfirm={handleConfirmDeletePart}
                        onCancel={() => setDeleteConfirmOpen(false)}
                    />
                </>
            }
            actions={
                <div className="Sale-part-modal-footer-actions flex w-full min-w-0 flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                        {!statusReceived && (
                            <Button
                                type="button"
                                variant="successOutline"
                                size="sm"
                                onClick={() => setConfirmReceivedOpen(true)}
                                disabled={confirmReceivedSaving}
                                leadingIcon={
                                    <i className="fas fa-check-circle" aria-hidden="true" />
                                }
                            >
                                Mark as received
                            </Button>
                        )}
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        {editForm && !isEditing && (
                            <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => setDeleteConfirmOpen(true)}
                            >
                                Remove
                            </Button>
                        )}
                        {!isEditing ? (
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSaveEdit}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            }
        >
            <form className="Sale-orderform Sale-orderform-modal Sale-part-modal-form">
                <div className="Sale-part-modal-row">
                    <div className="Sale-order-form-input Sale-order-form-input-date Sale-part-modal-field">
                        <label htmlFor="editPartsListDate">Order Date</label>
                        {isEditing ? (
                            <input
                                id="editPartsListDate"
                                type="date"
                                value={draftPart?.dateOrdered || ""}
                                onChange={(event) =>
                                    handleDraftChange("dateOrdered", event.target.value)
                                }
                                onBlur={onFieldBlur}
                            />
                        ) : (
                            <p className="Sale-modal-readonly">
                                {readOnlyValue(draftPart?.dateOrdered)}
                            </p>
                        )}
                    </div>

                    <div className="Sale-order-form-input Sale-part-modal-field">
                        <label htmlFor="editPartsListSupplier">Supplier</label>
                        {isEditing ? (
                            <input
                                id="editPartsListSupplier"
                                type="text"
                                value={draftPart?.supplier || ""}
                                onChange={(event) =>
                                    handleDraftChange("supplier", event.target.value)
                                }
                                onBlur={onFieldBlur}
                            />
                        ) : (
                            <p className="Sale-modal-readonly">
                                {readOnlyValue(draftPart?.supplier)}
                            </p>
                        )}
                    </div>
                </div>

                <div className="Sale-order-form-input Sale-part-modal-field">
                    <label htmlFor="editPartsListItem">Item</label>
                    {isEditing ? (
                        <input
                            id="editPartsListItem"
                            type="text"
                            value={draftPart?.item || ""}
                            onChange={(event) => handleDraftChange("item", event.target.value)}
                            onBlur={onFieldBlur}
                        />
                    ) : (
                        <p className="Sale-modal-readonly">{readOnlyValue(draftPart?.item)}</p>
                    )}
                </div>

                <div className="Sale-order-form-input Sale-order-form-input-qty Sale-part-modal-field">
                    <label htmlFor="editPartsListQuantity">Qty</label>
                    {isEditing ? (
                        <input
                            id="editPartsListQuantity"
                            type="number"
                            value={draftPart?.quantity || ""}
                            onChange={(event) => handleDraftChange("quantity", event.target.value)}
                            onBlur={onFieldBlur}
                        />
                    ) : (
                        <p className="Sale-modal-readonly">{readOnlyValue(draftPart?.quantity)}</p>
                    )}
                </div>

                <div className="Sale-order-form-input Sale-part-modal-field Sale-part-modal-field-full">
                    <label htmlFor="editPartsListNote">Note</label>
                    {isEditing ? (
                        <textarea
                            id="editPartsListNote"
                            value={draftPart?.note || ""}
                            onChange={(event) => handleDraftChange("note", event.target.value)}
                            onBlur={onFieldBlur}
                            rows={4}
                        />
                    ) : (
                        <p className="Sale-modal-readonly Sale-modal-readonly-multiline">
                            {readOnlyValue(draftPart?.note)}
                        </p>
                    )}
                </div>

                {showReceivedSection && (
                    <section
                        className={`Sale-part-modal-received-section Sale-part-modal-received-section--after-notes${
                            receivedSectionPending
                                ? " Sale-part-modal-received-section--pending"
                                : ""
                        }`}
                        aria-labelledby="part-received-section-title"
                    >
                        <h4
                            id="part-received-section-title"
                            className="Sale-part-modal-received-heading"
                        >
                            Received details
                        </h4>
                        <div className="Sale-part-modal-row Sale-part-modal-received-row">
                            <div className="Sale-order-form-input Sale-part-modal-field">
                                <label htmlFor="part-detail-item-received">Item received</label>
                                {isEditing ? (
                                    <div className="Sale-part-modal-received-checkbox-wrap">
                                        <Checkbox
                                            id="part-detail-item-received"
                                            checked={Boolean(draftPart?.itemReceived)}
                                            onChange={(event) =>
                                                handleDraftChange(
                                                    "itemReceived",
                                                    event.target.checked,
                                                )
                                            }
                                            ariaLabel="Part item received"
                                        />
                                        <span className="Sale-part-modal-received-checkbox-label">
                                            Received
                                        </span>
                                    </div>
                                ) : (
                                    <p
                                        id="part-detail-item-received-readonly"
                                        className="Sale-modal-readonly"
                                    >
                                        {statusReceived ? "Yes" : "No"}
                                    </p>
                                )}
                            </div>
                            <div className="Sale-order-form-input Sale-part-modal-field">
                                <label htmlFor="part-detail-date-received">Date received</label>
                                {isEditing ? (
                                    <input
                                        id="part-detail-date-received"
                                        type="date"
                                        value={dateReceivedInputValue}
                                        onChange={(event) =>
                                            handleDraftChange("dateReceived", event.target.value)
                                        }
                                        onBlur={onFieldBlur}
                                        disabled={!draftPart?.itemReceived}
                                    />
                                ) : (
                                    <p
                                        id="part-detail-date-received-readonly"
                                        className="Sale-modal-readonly"
                                    >
                                        {dateReceivedDisplay}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {isEditing && (
                    <div className="Sale-part-modal-delete-zone">
                        <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => setDeleteConfirmOpen(true)}
                        >
                            Delete
                        </Button>
                    </div>
                )}
            </form>
        </DetailModal>
    );
};

export default SaleEditPartModal;
